import React, { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import {
  useTripsAssignment,
  useMultiloadRoutes,
  useAssignmentLogs,
  useDriverEligibility,
} from '../queries/useAssignmentChunks';
import { useAssignmentRouteEnabled } from '../hooks/useAssignmentRouteEnabled';
import SchedulerCard from '../components/SchedulerCard';
import PlanningWindowCard from '../components/PlanningWindowCard';
import { useTimeFilter } from '../contexts/TimeFilterContext';
import { TripDetailsProvider, useTripDetails } from '../contexts/TripDetailsContext';
import { PickupEstimateMeta, DropoffEstimateMeta } from '../components/TripEstimateInline';
import TripFreeTimeBanner from '../components/TripFreeTimeBanner';
import useReverseGeocode from '../hooks/useReverseGeocode';
import { copyTextToClipboard } from '../utils/clipboard';
import { sortTripsForDisplay, buildTripSections } from '../utils/tripSections';
import { getMultiloadGroupP1Trip } from '../utils/tripFreeTime';
import { sumDeadheadMilesForTotal } from '../utils/deadheadEta';
import {
  tripMatchesTripsPageSearch,
  driverNameMatchesSearch,
} from '../utils/tripPageSearch';
import { getSessionTripCounts } from '../utils/sessionTripCounts';
import { clientNameWithLeg } from '../utils/groupLeg';
import { Tooltip, createTheme, ThemeProvider } from '@mui/material';
// import { FiCar } from 'react-icons/fi';
import { MdDirectionsCar } from 'react-icons/md';
import { RiRestTimeFill } from 'react-icons/ri';
import { Truck, MapPin, Check, Copy, Home, AlertTriangle, FileText, BarChart3, ChevronDown, ChevronRight, X, CheckCircle, ClipboardList, UserPlus, ScrollText } from 'lucide-react';
import CompanyNoteIconButton, { getCompanyNote } from '../components/CompanyNoteIconButton';
import TripRevenueLabel from '../components/TripRevenueLabel';
import { formatTripRevenue, sumTripRevenue } from '../utils/tripRevenue';
import DriverCardIdentity from '../components/DriverCardIdentity';
import FleetWindowHeader from '../components/FleetWindowHeader';
import TripBucketList, { TripLayoutSwitch } from '../components/TripBucketList';
import { groupTripsByWindow, isFleetDriver, lastWindowAwayMiles } from '../utils/fleetWindows';


// Custom theme for tooltips with optimized memory management
const tooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      defaultProps: {
        disableInteractive: false,
        enterDelay: 300,
        leaveDelay: 0,
        enterTouchDelay: 700,
        disableFocusListener: true,
        disableHoverListener: false,
        disableTouchListener: false,
        followCursor: false,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1f2937',
          color: '#f9fafb',
          fontSize: '0.875rem',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          border: '1px solid #374151',
          maxWidth: '300px',
        },
        arrow: {
          color: '#1f2937',
          '&::before': {
            border: '1px solid #374151',
          },
        },
      },
    },
  },
});

// Haversine distance calculation utility
const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
  if (!lat1 || !lng1 || !lat2 || !lng2) return 0;

  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Trips page only: omit driver cards when that driver's bucket is empty. (Bucket page and others unchanged.)
const driverHasTripsInBucket = (driver) =>
  (driver.trips || []).length > 0 || (driver.trip_count ?? 0) > 0;

// Fleet Image Component with Hover Effect
const FleetImageWithHover = ({ imageUrl, title, driverName }) => {
  const [showEnlarged, setShowEnlarged] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState({ top: 0, left: 0 });
  const imageRef = React.useRef(null);

  const updateTooltipPosition = React.useCallback(() => {
    if (imageRef.current && showEnlarged) {
      const rect = imageRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const tooltipHeight = 120;
      const tooltipWidth = 120;

      // Default: position above the image
      let top = rect.top - tooltipHeight - 10;
      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);

      // If it would go off the top, position below
      if (top < 10) {
        top = rect.bottom + 10;
      }

      // If it would go off the left, align to left edge
      if (left < 10) {
        left = 10;
      }

      // If it would go off the right, align to right edge
      if (left + tooltipWidth > viewportWidth - 10) {
        left = viewportWidth - tooltipWidth - 10;
      }

      setTooltipPosition({ top, left });
    }
  }, [showEnlarged]);

  React.useEffect(() => {
    if (showEnlarged) {
      updateTooltipPosition();
      // Update position on scroll or resize
      const handleUpdate = () => updateTooltipPosition();
      window.addEventListener('scroll', handleUpdate);
      window.addEventListener('resize', handleUpdate);

      return () => {
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [showEnlarged, updateTooltipPosition]);

  React.useEffect(() => {
    console.log('Fleet image component data:', {
      driverName,
      imageUrl,
      title,
      hasImage: !!imageUrl,
      imageError
    });
  }, [driverName, imageUrl, title, imageError]);

  // If there's no image URL or if the image failed to load, show fallback icon
  if (!imageUrl || !imageUrl.trim() || imageError) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <MdDirectionsCar className="text-yellow-300 w-full h-full cursor-pointer p-0.5" />
      </div>
    );
  }

  return (
    <>
      <div
        ref={imageRef}
        className="w-full h-full"
        onMouseEnter={() => setShowEnlarged(true)}
        onMouseLeave={() => setShowEnlarged(false)}
      >
        <img
          src={imageUrl}
          alt={`${title} icon`}
          className="w-full h-full rounded-full object-cover cursor-pointer transition-transform hover:scale-110"
          onError={(e) => {
            setImageError(true);
          }}
          onLoad={() => {
            setImageError(false);
          }}
        />
      </div>

      {/* Hover enlarged image - smart positioning */}
      {showEnlarged && !imageError && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 9999
          }}
        >
          <img
            src={imageUrl}
            alt={`${title} enlarged`}
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '8px',
              objectFit: 'cover',
              border: '3px solid white',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
            }}
          />
        </div>
      )}
    </>
  );
};

const getMockSelectedSessionData = () => ({
  isPublished: false,
  summary: {
    total_trips: 140,
    assigned_trips: 110,
    unassigned_trips: 30,
    assignment_rate: '78.57%',
    active_drivers: 22,
    total_drivers: 25,
    planning_time_seconds: 38.7,
    method: 'Mock Data - Selected Session',
  },
  api_usage: {
    total_calls: 380,
    successful_calls: 365,
    failed_calls: 15,
    success_rate: '96.05%',
    api_enabled: false,
  },
  drivers: [
    {
      driver_id: 'MOCK_SEL_001',
      driver_name: 'Selected Driver 1',
      total_trips: 5,
      total_distance_miles: 32.8,
      trips: [],
    },
  ],
  unassigned_trips: [
    {
      trip_id: 'MOCK_SEL_UNASSIGNED',
      client_name: 'Selected Client',
      schedule_time: '2025-11-14T12:00:00-08:00',
      appointment_time: '2025-11-14T12:15:00-08:00',
      reason: 'Selected session mock data',
    },
  ],
});

const TripsPage = () => {
  const {
    selectedResultId,
    setOnPublishSuccess,
    refreshAssignmentSession,
    patchAssignmentSessionCache,
  } = useTimeFilter();
  const isMockSession = Boolean(selectedResultId && String(selectedResultId).startsWith('mock-'));
  const tripsRouteEnabled = useAssignmentRouteEnabled('trips');
  const [mockAssignmentData, setMockAssignmentData] = useState(null);
  const [error, setError] = useState(null);
  const [showUnassignedPanel, setShowUnassignedPanel] = useState(false);
  const [showLogsPanel, setShowLogsPanel] = useState(false);
  const [initialLogsSearch, setInitialLogsSearch] = useState('');
  const [showDriverEligibilityPanel, setShowDriverEligibilityPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [tripLayout, setTripLayout] = useState('board');
  const tripsQuery = useTripsAssignment(selectedResultId, {
    enabled: tripsRouteEnabled && !isMockSession,
  });
  const multiloadQuery = useMultiloadRoutes(selectedResultId, {
    enabled: tripsRouteEnabled && !isMockSession,
  });
  const assignmentData = useMemo(() => {
    if (isMockSession) return mockAssignmentData;
    if (!tripsQuery.normalized) return null;
    return {
      ...tripsQuery.normalized,
      multiload_routes:
        multiloadQuery.chunkMap?.multiload_routes ||
        tripsQuery.normalized.multiload_routes ||
        {},
    };
  }, [isMockSession, mockAssignmentData, tripsQuery.normalized, multiloadQuery.chunkMap]);
  const loadStage = isMockSession ? 'full' : tripsQuery.loadStage;
  const sessionAuxEnabled = tripsRouteEnabled && !isMockSession && Boolean(selectedResultId);
  const logsQuery = useAssignmentLogs(selectedResultId, {
    enabled: sessionAuxEnabled,
  });
  const eligibilityQuery = useDriverEligibility(selectedResultId, {
    enabled: sessionAuxEnabled,
  });
  const loading = isMockSession
    ? false
    : tripsQuery.isLoading && !assignmentData;
  const loadingTripDetails = !isMockSession && tripsQuery.isLoadingDetails;

  const tripCounts = useMemo(
    () => getSessionTripCounts(assignmentData, { loadStage }),
    [assignmentData, loadStage]
  );

  const visibleDrivers = useMemo(() => {
    const timezone = assignmentData?.metadata?.timezone || 'America/Los_Angeles';
    const spaceTypeMap = assignmentData?.metadata?.load_unload_times || {};
    const toMs = (iso) => {
      if (!iso) return 0;
      const t = new Date(iso).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    return [...(assignmentData?.drivers || [])]
      .filter(driverHasTripsInBucket)
      .filter((driver) => {
        if (!debouncedSearchTerm) return true;
        return driverNameMatchesSearch(driver.driver_name, debouncedSearchTerm)
          || (driver.trips || []).some((trip) =>
            tripMatchesTripsPageSearch(trip, debouncedSearchTerm, spaceTypeMap, timezone)
          );
      })
      .sort((a, b) => {
        const start = toMs(a.shift_window?.shift_start) - toMs(b.shift_window?.shift_start);
        if (start !== 0) return start;
        return toMs(a.shift_window?.shift_end) - toMs(b.shift_window?.shift_end);
      });
  }, [assignmentData, debouncedSearchTerm]);

  const mockLogsData = useMemo(() => {
    if (!isMockSession || !selectedResultId) return null;
    return {
      result_id: selectedResultId,
      log_count: 50,
      logs: Array.from({ length: 50 }, (_, i) => ({
        timestamp: new Date(Date.now() - i * 60000).toISOString().slice(0, 19),
        trip_id: `TRIP${String(i + 1).padStart(4, '0')}`,
        trip_client: `Client_${(i % 8) + 1}`,
        driver_name: `Driver_${(i % 12) + 1}`,
        assigned: (i % 5 !== 0).toString(),
        assignment_reason: i % 5 === 0 ? `Failed constraint ${(i % 9) + 1}` : 'Successfully assigned',
      })),
      summary: { total_trips: 150, assigned_trips: 115, unassigned_trips: 35, active_drivers: 25 },
    };
  }, [isMockSession, selectedResultId]);

  const logsData = useMemo(() => {
    if (isMockSession) return mockLogsData;
    return logsQuery.data ?? null;
  }, [isMockSession, mockLogsData, logsQuery.data]);

  const logsLoading = isMockSession
    ? false
    : (logsQuery.isLoading || logsQuery.isFetching) && !logsQuery.data;

  const eligibilityData = useMemo(() => eligibilityQuery.data ?? null, [eligibilityQuery.data]);

  // Debounce search term to avoid too many re-renders while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePublishSuccess = useCallback((publishedType) => {
    if (!selectedResultId || isMockSession) return;
    patchAssignmentSessionCache(selectedResultId, {
      publishFlags: publishedType === 'portal'
        ? { portalPlanPublished: true }
        : { isPublished: true },
    });
  }, [selectedResultId, isMockSession, patchAssignmentSessionCache]);

  useEffect(() => { setOnPublishSuccess(() => handlePublishSuccess); return () => setOnPublishSuccess(null); }, [handlePublishSuccess, setOnPublishSuccess]);

  useEffect(() => {
    if (!selectedResultId) {
      setMockAssignmentData(null);
      setError(null);
      setShowLogsPanel(false);
      setShowUnassignedPanel(false);
      setShowDriverEligibilityPanel(false);
      return;
    }

    if (isMockSession) {
      setMockAssignmentData(getMockSelectedSessionData());
      setError(null);
      return;
    }

    if (tripsQuery.isLoading || tripsQuery.isLoadingDetails) {
      setError(null);
      return;
    }

    if (tripsQuery.error && !tripsQuery.normalized) {
      console.error('Error fetching selected data:', tripsQuery.error);
      setError('Failed to fetch selected assignment data');
      return;
    }

    const normalized = tripsQuery.normalized;

    if (normalized) {
      setError(null);
    } else if (!tripsQuery.isFetching) {
      setError('Selected assignment data not found');
    }
  }, [
    selectedResultId,
    isMockSession,
    tripsQuery.normalized,
    tripsQuery.error,
    tripsQuery.isLoading,
    tripsQuery.isLoadingDetails,
    tripsQuery.isFetching,
  ]);

  const handleShowLogs = (tripId = '') => {
    if (selectedResultId) {
      setInitialLogsSearch(tripId);
      setShowLogsPanel(true);
    }
  };

  const refreshData = useCallback(() => {
    console.log('Refreshing dashboard data after sync...', { selectedResultId });
    if (selectedResultId && !isMockSession) {
      refreshAssignmentSession(selectedResultId);
    }
  }, [selectedResultId, isMockSession, refreshAssignmentSession]);

  const hasData = assignmentData && (loadStage === 'full' ? assignmentData.drivers : assignmentData.summary);
  const companyTimezone = assignmentData?.metadata?.timezone || 'America/Los_Angeles';
  const tripSearchSpaceTypeMap = assignmentData?.metadata?.load_unload_times || {};

  return (
    <TripDetailsProvider>
      <div className="relative">
        <div className="space-y-2">
          {!hasData ? (
            <>
              <SchedulerCard onSyncComplete={refreshData} />
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-gray-50/30 rounded-2xl border border-dashed border-gray-200 mt-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-main-primary mb-4"></div>
                  <p className="text-sm font-medium text-gray-500 italic font-mono">Synchronizing planning data...</p>
                </div>
              ) : error ? (
                <div className="mt-8 mx-auto max-w-2xl bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <span className="text-sm font-semibold truncate max-w-md">Error: {error}</span>
                  </div>
                  <button onClick={refreshData} className="px-4 py-1.5 bg-red-600 text-white rounded-md text-xs font-bold hover:bg-red-700 transition-colors whitespace-nowrap">
                    Retry Refresh
                  </button>
                </div>
              ) : (
                <div className="text-center py-20 bg-gray-50/30 border border-dashed border-gray-200 rounded-2xl mt-4">
                  <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-gray-900 mb-2 font-mono uppercase tracking-tight">No Data Selected</h2>
                  <p className="text-sm text-gray-500 max-w-[280px] mx-auto">Please select a planning session from the timestamp dropdown to view assignment results.</p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Merged Card: 1 header row + 1 stats row (8 stats total) */}
              {(() => {
                const { assignedCount, unassignedCount, totalCount, newAssignmentCount } = tripCounts;

                return (
                  <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
                    <SchedulerCard
                      embedded
                      onSyncComplete={refreshData}
                      headerTitleExtra={
                        <>
                          <div className="bg-dashboard-stats-primary-dark rounded-lg p-1.5 flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-dashboard-stats-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5a2.25 2.25 0 002.25 2.25h.75" />
                            </svg>
                          </div>
                          <span className="text-[11px] font-semibold text-dashboard-stats-primary whitespace-nowrap">Trip Assignments</span>
                          {assignmentData?.metadata?.company_name && (
                            <span className="text-[10px] text-dashboard-stats-info hidden sm:inline truncate">— {assignmentData.metadata.company_name}</span>
                          )}
                          <div className="w-px h-4 bg-dashboard-stats-primary/25 flex-shrink-0" />
                          <PlanningWindowCard assignmentData={assignmentData} />
                        </>
                      }
                      headerActionsExtra={
                        <>
                          {assignmentData?.unassigned_trips && assignmentData.unassigned_trips.length > 0 && (
                            <button
                              onClick={() => setShowUnassignedPanel(true)}
                              className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-main-primary text-white hover:bg-main-primary/90 transition-colors"
                            >
                              Unassigned ({assignmentData.unassigned_trips.length})
                            </button>
                          )}
                          <button
                            onClick={() => setShowDriverEligibilityPanel(true)}
                            className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-main-primary text-white hover:bg-main-primary/90 transition-colors"
                          >
                            Eligibility
                            {eligibilityData?.eligible_drivers != null && eligibilityData?.total_drivers != null
                              ? ` (${eligibilityData.eligible_drivers}/${eligibilityData.total_drivers})`
                              : eligibilityQuery.isLoading
                                ? ' …'
                                : ''}
                          </button>
                          <button
                            onClick={() => handleShowLogs('')}
                            disabled={!selectedResultId}
                            className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-main-primary text-white hover:bg-main-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={!selectedResultId ? "Select a planning session first" : "View detailed assignment logs"}
                          >
                            Logs
                          </button>
                        </>
                      }
                      statsExtra={
                        <>
                          <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                            <dl className="flex items-center h-full">
                              <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                                <MapPin className="text-dashboard-stats-primary h-3.5 w-3.5" />
                              </dt>
                              <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                                <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Total Trips</div>
                                <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">{totalCount}</div>
                              </dd>
                            </dl>
                          </div>

                          <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                            <dl className="flex items-center h-full">
                              <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                                <CheckCircle className="text-dashboard-stats-primary h-3.5 w-3.5" />
                              </dt>
                              <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                                <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Assigned</div>
                                <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">{assignedCount}</div>
                              </dd>
                            </dl>
                          </div>

                          <div
                            className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]"
                            title="Trips marked New Assignment in driver buckets (updates live with manual edits)"
                          >
                            <dl className="flex items-center h-full">
                              <dt className="bg-fuchsia-600/15 rounded-lg p-2 flex-shrink-0">
                                <UserPlus className="text-fuchsia-600 h-3.5 w-3.5" />
                              </dt>
                              <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                                <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">New Assignment</div>
                                <div className="text-fuchsia-700 truncate text-xs font-bold tabular-nums">{newAssignmentCount ?? 0}</div>
                              </dd>
                            </dl>
                          </div>

                          <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                            <dl className="flex items-center h-full">
                              <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                                <AlertTriangle className="text-dashboard-stats-primary h-3.5 w-3.5" />
                              </dt>
                              <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                                <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Unassigned</div>
                                <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">{unassignedCount}</div>
                              </dd>
                            </dl>
                          </div>

                          <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
                            <dl className="flex items-center h-full">
                              <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                                <Truck className="text-dashboard-stats-primary h-3.5 w-3.5" />
                              </dt>
                              <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                                <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Planned Drivers</div>
                                <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">
                                  {(assignmentData.drivers || []).filter(driverHasTripsInBucket).length}
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </>
                      }
                    />
                  </div>
                );
              })()}

              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-gray-50/30 rounded-2xl border border-dashed border-gray-200 mt-6">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-main-primary mb-4"></div>
                  <p className="text-sm font-medium text-gray-500 italic font-mono">Updating dashboard view...</p>
                </div>
              ) : error ? (
                <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <span className="text-sm font-semibold truncate max-w-lg">Error: {error}</span>
                  </div>
                  <button onClick={refreshData} className="px-4 py-1.5 bg-red-600 text-white rounded-md text-xs font-bold hover:bg-red-700 transition-colors whitespace-nowrap">
                    Retry Synchronization
                  </button>
                </div>
              ) : (
                <>
                  {/* Search Input */}
              <div className="mt-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search drivers, clients, trip IDs, addresses, time, LOS or space type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <TripLayoutSwitch value={tripLayout} onChange={setTripLayout} />
                  {debouncedSearchTerm && (
                    <div className="text-sm text-textMuted">
                      {(() => {
                        const filteredDrivers = (assignmentData?.drivers || [])
                          .filter(driverHasTripsInBucket)
                          .filter((driver) => {
                            const driverNameMatch = driverNameMatchesSearch(
                              driver.driver_name,
                              debouncedSearchTerm
                            );
                            const tripMatches = driver.trips.some((trip) =>
                              tripMatchesTripsPageSearch(trip, debouncedSearchTerm, tripSearchSpaceTypeMap, companyTimezone)
                            );
                            return driverNameMatch || tripMatches;
                          });

                        const matchingTrips = filteredDrivers.reduce(
                          (sum, driver) =>
                            sum + driver.trips.filter((trip) =>
                              tripMatchesTripsPageSearch(trip, debouncedSearchTerm, tripSearchSpaceTypeMap, companyTimezone)
                            ).length,
                          0
                        );

                        return `Found ${filteredDrivers.length} driver${filteredDrivers.length !== 1 ? 's' : ''} with ${matchingTrips} matching trip${matchingTrips !== 1 ? 's' : ''}`;
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {loadStage !== 'shell' && (assignmentData.drivers || []).length > 0 && (
              <>
              {/* Drivers and Their Trips */}
              {loadingTripDetails && loadStage !== 'full' && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-main-primary" />
                  Loading trip details…
                </div>
              )}
              {tripLayout === 'list' ? (
                <TripBucketList
                  drivers={visibleDrivers}
                  timezone={companyTimezone}
                  spaceTypeMap={tripSearchSpaceTypeMap}
                  multiloadRoutes={assignmentData?.multiload_routes || {}}
                  searchTerm={debouncedSearchTerm}
                  onViewLogs={(tripId) => handleShowLogs(tripId)}
                  canViewLogs={!!selectedResultId}
                />
              ) : (
              <div className="flex items-stretch gap-3 overflow-x-auto pb-2 custom-scrollbar h-[calc(100vh-232px)]">
                {visibleDrivers.map((driver) => (
                    <DriverCard
                      key={driver.driver_id}
                      driver={driver}
                      timezone={companyTimezone}
                      spaceTypeMap={assignmentData.metadata?.load_unload_times || {}}
                      searchTerm={debouncedSearchTerm}
                      multiloadRoutes={assignmentData?.multiload_routes || {}}
                      onViewLogs={(tripId) => handleShowLogs(tripId)}
                      canViewLogs={!!selectedResultId}
                    />
                  ))}
              </div>
              )}
              </>
              )}
                </>
              )}
            </>
          )}
        </div>


        {/* Sliding Unassigned Trips Panel */}
        {showUnassignedPanel && (
          <UnassignedTripsPanel
            unassignedTrips={assignmentData?.unassigned_trips || []}
            onClose={() => setShowUnassignedPanel(false)}
            onViewLogs={(tripId) => {
              setShowUnassignedPanel(false); // Close unassigned panel
              handleShowLogs(tripId); // Open logs with trip ID search
            }}
            canViewLogs={!!selectedResultId}
            timezone={companyTimezone}
            spaceTypeMap={tripSearchSpaceTypeMap}
          />
        )}

        {/* Sliding Assignment Logs Panel */}
        {showLogsPanel && (
          <LogsPanel
            logsData={logsData}
            logsLoading={logsLoading}
            tripCounts={tripCounts}
            onClose={() => setShowLogsPanel(false)}
            initialSearch={initialLogsSearch}
            timezone={companyTimezone}
          />
        )}

        {/* Sliding Driver Eligibility Panel */}
        {showDriverEligibilityPanel && (
          <DriverEligibilityPanel
            eligibilityData={eligibilityData}
            eligibilityLoading={eligibilityQuery.isLoading && !eligibilityData}
            onClose={() => setShowDriverEligibilityPanel(false)}
          />
        )}
      </div>
    </TripDetailsProvider>
  );
};

const DriverCard = ({ driver, timezone, spaceTypeMap = {}, searchTerm = '', multiloadRoutes = {}, onViewLogs, canViewLogs = false }) => {
  const [copiedCurrentCoords, setCopiedCurrentCoords] = React.useState(false);

  // Add search highlight styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .search-highlight {
        animation: searchHighlight 2s ease-in-out;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5) !important;
      }
      @keyframes searchHighlight {
        0% { box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5); }
        50% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.3); }
        100% { box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const [copiedCurrentAddress, setCopiedCurrentAddress] = React.useState(false);
  const [copiedRefCoords, setCopiedRefCoords] = React.useState(false);
  const [copiedRefAddress, setCopiedRefAddress] = React.useState(false);
  const [locationsOpen, setLocationsOpen] = React.useState(false);
  const [showAvailableIn, setShowAvailableIn] = React.useState(false);
  const { showTripDetails } = useTripDetails();

  // Keyboard listener for Shift+L to toggle Available In visibility
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Shift + L to toggle available in display
      if (e.shiftKey && (e.key === 'L' || e.key === 'l')) {
        setShowAvailableIn(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-scroll to first matching trip ONLY when search term is active
  React.useEffect(() => {
    if (searchTerm) {
      const sortedTrips = [...driver.trips].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));

      // Find first matching trip
      const firstMatchingTripIndex = sortedTrips.findIndex((trip) =>
        tripMatchesTripsPageSearch(trip, searchTerm, spaceTypeMap, timezone)
      );

      if (firstMatchingTripIndex !== -1) {
        // Scroll to the trip card after a short delay to allow rendering
        setTimeout(() => {
          const tripElement = document.querySelector(`[data-trip-id="${sortedTrips[firstMatchingTripIndex].trip_id || sortedTrips[firstMatchingTripIndex].id}"]`);
          if (tripElement) {
            tripElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add temporary highlight
            tripElement.classList.add('search-highlight');
            setTimeout(() => {
              tripElement.classList.remove('search-highlight');
            }, 2000);
          }
        }, 100);
      }
    }
  }, [searchTerm, driver.driver_id, timezone]); // Removed driver.trips to stop snapping on data updates

  // Get driver's current GPS location coordinates
  const currentLat = driver.current_latitude || driver.driver_latitude || driver.latitude;
  const currentLng = driver.current_longitude || driver.driver_longitude || driver.longitude;
  const hasCurrentLocation = currentLat && currentLng;
  const currentLocationText = hasCurrentLocation ? `${currentLat}, ${currentLng}` : null;

  // Get driver's reference location (used for first trip assignment)
  // For busy drivers: current trip dropoff | For non-busy: same as current location
  const refLat = driver.ref_latitude;
  const refLng = driver.ref_longitude;
  const hasRefLocation = refLat && refLng;
  const refLocationText = hasRefLocation ? `${refLat}, ${refLng}` : null;

  // Check if ref location is different from current (driver is busy)
  const isDifferentRef = hasCurrentLocation && hasRefLocation &&
    (currentLat !== refLat || currentLng !== refLng);

  // Calculate "Away from Home" distance using takeMeHome coordinates
  const calculateAwayFromHome = () => {
    // Find the last trip's dropoff location (last trip in the sorted list)
    const sortedTrips = [...driver.trips].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
    const lastTrip = sortedTrips[sortedTrips.length - 1];

    if (!lastTrip) return 0; // No trips assigned

    // Get dropoff coordinates from the last trip
    const dropoffLat = lastTrip.dropoff_latitude || lastTrip.dropoff_lat;
    const dropoffLng = lastTrip.dropoff_longitude || lastTrip.dropoff_lng;

    if (!dropoffLat || !dropoffLng) return 0; // No dropoff coordinates

    // Use takeMeHome coordinates as the home location
    let homeLat, homeLng;

    if (driver.takeMeHome && Array.isArray(driver.takeMeHome) && driver.takeMeHome.length >= 2) {
      // takeMeHome format: [longitude, latitude]
      homeLng = driver.takeMeHome[0]; // longitude
      homeLat = driver.takeMeHome[1]; // latitude
    } else {
      // Fallback to previous logic if takeMeHome is not available
      console.warn(`takeMeHome not available for driver ${driver.driver_id}, using fallback logic`);
      if (hasRefLocation && isDifferentRef) {
        homeLat = refLat;
        homeLng = refLng;
      } else {
        homeLat = currentLat;
        homeLng = currentLng;
      }
    }

    if (!homeLat || !homeLng) return 0; // No home location available

    // Calculate distance from last dropoff to home
    return calculateHaversineDistance(dropoffLat, dropoffLng, homeLat, homeLng);
  };

  const awayFromHomeMiles = isFleetDriver(driver) ? lastWindowAwayMiles(driver) : calculateAwayFromHome();
  const fleetMode = isFleetDriver(driver);
  const fleetCount = (driver.fleet_windows || []).length;

  // Reverse geocode both locations
  const { address: currentAddress, loading: currentAddressLoading } = useReverseGeocode(currentLat, currentLng);
  const { address: refAddress, loading: refAddressLoading } = useReverseGeocode(refLat, refLng);

  const copyToClipboard = async (text, setter) => {
    if (!text) return;
    const success = await copyTextToClipboard(text);
    if (success) {
      setter(true);
      setTimeout(() => setter(false), 2000);
    } else {
      // silently fail but log for diagnostics in production
      console.warn('Copy to clipboard failed for text:', text);
    }
  };

  // Format shift time helper
  const formatShiftTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone || 'America/Los_Angeles' });
    } catch { return ''; }
  };

  const shiftLabel = driver.shift_window?.has_shift
    ? `${formatShiftTime(driver.shift_window.shift_start)}—${formatShiftTime(driver.shift_window.shift_end)}`
    : '—';

  const tripCount = driver.trips?.filter(t => !t.is_break).length ?? driver.total_trips ?? 0;
  const totalMiles = (driver.trips?.reduce((sum, trip) => sum + (Number(trip.trip_distance_miles) || Number(trip.trip_distance) || 0), 0) ?? driver.total_distance_miles ?? 0);
  const totalDeadhead = sumDeadheadMilesForTotal(driver.trips);
  const totalRevenue = sumTripRevenue(driver.trips);

  return (
    <div className="w-96 flex-shrink-0 h-full flex flex-col rounded-md border border-main-base cursor-pointer bg-white" data-driver-id={driver.driver_id}>
      {/* === BUCKET HEADER === */}
      {/* DETAILED BUCKET HEADER */}
      {/* REFINED DETAILED BUCKET HEADER */}
      <div className="bg-main-primary text-white p-2 rounded-t-md space-y-2 select-none shadow-lg">
        {/* Identity Row */}
        <DriverCardIdentity
          driver={driver}
          fleetMode={fleetMode}
          shiftLabel={shiftLabel}
          timezone={timezone}
        />

        {/* Stats Summary Grid Bar */}
        <div className="overflow-hidden rounded-md border border-white/5 bg-white/5 text-center">
           <div className="grid grid-cols-4 items-center py-1">
              <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap border-r border-white/5 px-1.5" title="Loaded Miles">
                 <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">LM:</span>
                 <span className="text-[10px] font-black leading-none tabular-nums">{totalMiles.toFixed(1)}mi</span>
              </div>
              <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap border-r border-white/5 px-1.5">
                 <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">DH:</span>
                 <span className="text-[10px] font-black leading-none tabular-nums text-yellow-500">{totalDeadhead.toFixed(1)}mi</span>
              </div>
              <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap border-r border-white/5 px-1.5">
                 {awayFromHomeMiles > 0 ? (
                   <>
                      <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">Away:</span>
                      <span className="inline-flex items-baseline gap-0.5 text-[10px] font-black leading-none tabular-nums text-blue-300"><Home className="h-2 w-2 shrink-0 self-center" />{awayFromHomeMiles.toFixed(1)}mi</span>
                   </>
                 ) : (
                   <span className="text-[10px] font-black leading-none opacity-10">—</span>
                 )}
              </div>
              <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap px-1.5">
                 <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">Total:</span>
                 <span className="text-[10px] font-black leading-none tabular-nums">{(totalMiles + totalDeadhead + (awayFromHomeMiles || 0)).toFixed(1)}mi</span>
              </div>
           </div>
           <TripRevenueLabel
             value={totalRevenue}
             variant="header"
             middleLabel={fleetMode ? 'Fleet' : ''}
             middleValue={fleetMode ? (fleetCount || '—') : ''}
             tripCount={tripCount}
           />
        </div>

        {(hasCurrentLocation || hasRefLocation || driver.home_address) && (
          <div className="mt-1">
            <button
              type="button"
              aria-expanded={locationsOpen}
              aria-label={locationsOpen ? 'Hide current GPS and starting point' : 'Show current GPS and starting point'}
              onClick={(e) => {
                e.stopPropagation();
                setLocationsOpen((open) => !open);
              }}
              className="flex w-full items-center gap-1 rounded px-0.5 py-0.5 text-left hover:bg-white/10 transition-colors"
            >
              <ChevronDown
                className={`h-3 w-3 shrink-0 opacity-80 transition-transform ${locationsOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
              <span className="text-[6.5px] font-black uppercase tracking-[0.1em] leading-none opacity-70">
                Current GPS / Starting Point
              </span>
            </button>
            {locationsOpen && (
              <div className="space-y-1 mt-1">
                {hasCurrentLocation && (
                  <div className="space-y-0.5 group">
                     <div className="text-[6.5px] font-black uppercase opacity-60 tracking-[0.1em] leading-none mb-1">Current GPS:</div>
                     <div className="flex items-start justify-between gap-1 pl-0.5">
                        <div className="text-[8.5px] opacity-90 font-bold leading-tight flex-1 pr-4" title={currentAddress || currentLocationText}>
                           {currentAddress || 'Locating position...'}
                        </div>
                        <button onClick={() => copyToClipboard(currentAddress || currentLocationText, setCopiedCurrentAddress)} className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]">
                           {copiedCurrentAddress ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                        </button>
                     </div>
                     <div className="flex items-center justify-between gap-1 pl-0.5">
                        <span className="font-mono text-[8.5px] font-medium opacity-60 tracking-tighter leading-none">
                          {currentLat.toFixed(6)}, {currentLng.toFixed(6)}
                        </span>
                        <button onClick={() => copyToClipboard(currentLocationText, setCopiedCurrentCoords)} className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]">
                           {copiedCurrentCoords ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                        </button>
                     </div>
                  </div>
                )}

                {(hasRefLocation || driver.home_address) && (
                  <div className={`space-y-0.5 group ${hasCurrentLocation ? 'border-t border-white/5 pt-1.5 mt-1' : ''}`}>
                     <div className="text-[6.5px] font-black uppercase opacity-60 tracking-[0.1em] text-yellow-100 leading-none mb-1">Starting Point:</div>
                     <div className="flex items-start justify-between gap-1 pl-0.5">
                        <div className="text-[8.5px] opacity-90 font-bold leading-tight flex-1 text-yellow-100/90 pr-4" title={refAddress || refLocationText}>
                           {refAddress || 'Reference address...'}
                        </div>
                        <button onClick={() => copyToClipboard(refAddress || refLocationText, setCopiedRefAddress)} className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]">
                           {copiedRefAddress ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                        </button>
                     </div>
                     <div className="flex items-center justify-between gap-1 pl-0.5">
                        <span className="font-mono text-[8.5px] font-medium opacity-60 tracking-tighter leading-none text-yellow-100/70">
                          {refLat.toFixed(6)}, {refLng.toFixed(6)}
                        </span>
                        <button onClick={() => copyToClipboard(refLocationText, setCopiedRefCoords)} className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]">
                           {copiedRefCoords ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                        </button>
                     </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* === TRIPS LIST === */}
      <div className="flex-1 flex flex-col gap-y-2 overflow-y-auto p-2">
        {driver.trips.length === 0 ? (
          <p className="text-main-light text-center py-4 text-xs">No trips assigned</p>
        ) : (
          (() => {
            const tripElements = [];
            let prevTripEndTime = null;
            const windowGroups = fleetMode
              ? groupTripsByWindow(driver)
              : [{ window: undefined, trips: sortTripsForDisplay(driver.trips || []) }];

            windowGroups.forEach((group) => {
              if (group.window !== undefined) {
                tripElements.push(
                  <FleetWindowHeader
                    key={`fleet-hdr-${group.window == null ? 'none' : group.window.window_index}`}
                    window={group.window}
                    driver={driver}
                    trips={group.trips}
                    timezone={timezone}
                  />
                );
              }
              const sections = buildTripSections(group.trips);

            sections.forEach((section) => {
              const isMultiloadSection = section.multiloadGroupId != null;
              const content = [];

              if (isMultiloadSection) {
                const routeSteps = multiloadRoutes[section.multiloadGroupId];
                const p1Trip = getMultiloadGroupP1Trip(section);
                if (routeSteps && Array.isArray(routeSteps) && routeSteps.length > 0) {
                  const tripIdToLabel = {};
                  let n = 1;
                  routeSteps.forEach(({ trip_id }) => {
                    if (trip_id && tripIdToLabel[trip_id] == null) tripIdToLabel[trip_id] = n++;
                  });
                  const routeStr = routeSteps
                    .map(({ trip_id, stop_type }) => {
                      const label = tripIdToLabel[trip_id] || '?';
                      return stop_type === 'pickup' ? `P${label}` : `D${label}`;
                    })
                    .join(' → ');
                  content.push(
                    <div
                      key={`route-${section.multiloadGroupId}`}
                      className="border border-primary-border rounded-lg p-2 mb-2 bg-primary-light/40 space-y-1.5"
                    >
                      {p1Trip && (
                        <TripFreeTimeBanner
                          trip={p1Trip}
                          driverTrips={driver.trips}
                          timezone={timezone}
                          asMultiloadGroup
                          multiloadRoutes={multiloadRoutes}
                        />
                      )}
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <span className="text-primary font-semibold flex items-center gap-1"><Truck className="w-3 h-3" /> Route:</span>
                        <span className="font-mono font-semibold text-main-primary">{routeStr}</span>
                      </div>
                    </div>
                  );
                } else if (p1Trip) {
                  content.push(
                    <div key={`ml-freetime-${section.multiloadGroupId}`} className="mb-1">
                      <TripFreeTimeBanner
                        trip={p1Trip}
                        driverTrips={driver.trips}
                        timezone={timezone}
                        asMultiloadGroup
                        multiloadRoutes={multiloadRoutes}
                      />
                    </div>
                  );
                }
              }

              section.trips.forEach((trip, idxInSection) => {
                // Removed wait time indicators as requested

                const isBusy = trip.is_busy_trip || trip.sequence === 0;
                const etaValue = isBusy ? trip.available_in_minutes : trip.eta_to_pickup_minutes;
                const deadheadValue = trip.deadhead_miles || 0;

                const isMatchingTrip = searchTerm && tripMatchesTripsPageSearch(trip, searchTerm, spaceTypeMap, timezone);

                content.push(
                  <TripCard
                    key={trip.trip_id || trip.id || `idx-${idxInSection}`}
                    trip={trip}
                    timezone={timezone}
                    showAvailableIn={showAvailableIn}
                    showTripDetails={showTripDetails}
                    spaceTypeMap={spaceTypeMap}
                    isHighlighted={isMatchingTrip}
                    multiloadSectionSize={isMultiloadSection ? section.trips.length : undefined}
                    multiloadIndexInSection={isMultiloadSection ? idxInSection + 1 : undefined}
                    deadhead={deadheadValue}
                    eta={etaValue}
                    onViewLogs={onViewLogs}
                    canViewLogs={canViewLogs}
                    driverTrips={driver.trips}
                    multiloadRoutes={multiloadRoutes}
                  />
                );

                // Track end time for wait-time calculation
                const tripEndTime = trip.trip_details?.approximate_dropoff_time || trip.appointment_time;
                if (tripEndTime) prevTripEndTime = tripEndTime;
              });

              if (isMultiloadSection) {
                tripElements.push(
                  <div
                    key={`multiload-wrap-${section.multiloadGroupId}`}
                    className="border border-main-primary rounded-xl p-3 mb-3 bg-primary-light/20 space-y-2"
                  >
                    {content}
                  </div>
                );
              } else {
                content.forEach((el) => tripElements.push(el));
              }
            });
            });

            return tripElements;
          })()
        )}
      </div>
    </div>
  );
};

const TripCard = ({ trip, timezone, showAvailableIn = false, showTripDetails = false, spaceTypeMap = {}, isHighlighted = false, multiloadSectionSize, multiloadIndexInSection, deadhead, eta, onViewLogs, canViewLogs = false, driverTrips = null, multiloadRoutes = null }) => {
  const [copiedField, setCopiedField] = React.useState(null);

  const copyToClipboard = async (text, field) => {
    if (!text) return;
    const success = await copyTextToClipboard(text);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } else {
      console.warn('Copy to clipboard failed for text:', text);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone || 'America/Los_Angeles'
      });
    } catch {
      return isoString;
    }
  };

  // Format break time — same as BucketPage: ISO → company timezone; plain strings unchanged
  const formatBreakTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const str = String(timeStr);
    if (str.indexOf('T') === -1) return str;
    const tz = timezone || 'America/Los_Angeles';
    try {
      return new Date(timeStr).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: tz,
      });
    } catch {
      return str;
    }
  };

  // Check if this is the driver's current active trip (is_busy_trip flag)
  const isBusyTrip = trip.is_busy_trip === true;
  const isGroupTrip = trip.is_group_trip === true;
  const isBreak = trip.is_break === true;
  const manuallyAdjusted = trip.manually_adjusted === true;
  const groupIndex = trip.group_index || 0;  // 1, 2, 3... for group trips
  const groupTotal = trip.group_total || trip.trip_count || 1;

  // NEW: Multiload detection (use route order P1,P2,P3 when backend sends multiload_pickup_order)
  const isMultiload = trip.is_multiload === true;
  const multiloadGroupSize = isMultiload
    ? (multiloadSectionSize != null ? multiloadSectionSize : (trip.multiload_partner_ids?.length || 0) + 1)
    : 0;
  const multiloadPosition = isMultiload
    ? (trip.multiload_pickup_order ?? multiloadIndexInSection ?? (trip.multiload_partner_ids ? trip.multiload_partner_ids.filter(id => id < trip.trip_id).length + 1 : 0))
    : 0;
  const showMultiloadBadge = isMultiload && multiloadGroupSize > 1;
  const isMultiloadPrimary = trip.is_multiload_primary === true;
  const escortCount = Number(trip.no_of_escorts ?? trip.noOfEscorts ?? 0) || 0;
  const hasEscorts = escortCount > 0;

  const tripStatus = (trip.trip_status || '').toLowerCase();
  const busyStyles = (() => {
    switch (tripStatus) {
      case 'onway':
      case 'on way':
        return { card: 'border-blue-200 bg-blue-50/50', seq: 'bg-blue-100 text-blue-700', badge: 'bg-blue-500', label: 'text-blue-500', statusText: 'text-blue-700', divider: 'border-blue-100' };
      case 'arrived': 
        return { card: 'border-orange-200 bg-orange-50/50', seq: 'bg-orange-100 text-orange-700', badge: 'bg-orange-500', label: 'text-orange-500', statusText: 'text-orange-700', divider: 'border-orange-100' };
      case 'started':
      case 'pickedup':
        return { card: 'border-emerald-200 bg-emerald-50/50', seq: 'bg-emerald-100 text-emerald-700', badge: 'bg-emerald-500', label: 'text-emerald-500', statusText: 'text-emerald-700', divider: 'border-emerald-100' };
      default:
        return isBusyTrip 
          ? { card: 'border-sky-200 bg-sky-50/50', seq: 'bg-sky-100 text-sky-700', badge: 'bg-sky-500', label: 'text-sky-500', statusText: 'text-sky-700', divider: 'border-sky-100' }
          : {};
    }
  })();

  // Special rendering for break trips
  if (isBreak) {
    const breakDuration = trip.break_duration_minutes || 0;
    // Always format through formatBreakTime to handle both short and ISO formats
    const breakStartDisplay = formatBreakTime(trip.break_start || trip.schedule_time);
    const breakEndDisplay = formatBreakTime(trip.break_end || trip.appointment_time);

    return (
      <>
        <TripFreeTimeBanner trip={trip} driverTrips={driverTrips} timezone={timezone} multiloadRoutes={multiloadRoutes} />
        <div className="border border-dashed border-main-primary rounded-lg p-2 bg-teal-50/60 transition-all hover:shadow-sm">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                 {RiRestTimeFill && <RiRestTimeFill className="text-teal-600 text-sm" />}
              </div>
              <div className="min-w-0 flex items-center gap-2">
                <h4 className="font-bold text-main-dark text-xs leading-none truncate">
                  {clientNameWithLeg(trip.client_name || 'Rest Break', trip)}
                </h4>
                <span className="text-[8px] bg-main-primary text-white px-1.5 py-0.5 rounded font-black uppercase tracking-wider flex-shrink-0">
                  Break
                </span>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded bg-teal-100 text-teal-700 font-black">
              #{trip.sequence}
            </span>
          </div>
          
          {/* Stats Grid Bar - Derived from requested design style */}
          <div className="grid grid-cols-3 px-1 py-1.5 border border-teal-200/50 bg-white/50 rounded-md text-center items-center">
             <div className="flex items-center justify-center gap-1 border-r border-teal-200/30">
                <span className="opacity-60 text-[7px] font-black uppercase tracking-tighter text-teal-800">Duration:</span>
                <span className="text-[10px] font-black text-teal-900">{Math.round(breakDuration)}m</span>
             </div>
             <div className="flex items-center justify-center gap-1 border-r border-teal-200/30">
                <span className="opacity-60 text-[7px] font-black uppercase tracking-tighter text-teal-800">Start:</span>
                <span className="text-[10px] font-black text-teal-900">{breakStartDisplay}</span>
             </div>
             <div className="flex items-center justify-center gap-1">
                <span className="opacity-60 text-[7px] font-black uppercase tracking-tighter text-teal-800">End:</span>
                <span className="text-[10px] font-black text-teal-900">{breakEndDisplay}</span>
             </div>
          </div>
        </div>
      </>
    );
  }

  // Portal style MM/DD/YYYY HH:mm in company timezone (not browser local)
  const formatShortDateTime = (isoString) => {
    if (!isoString) return '';
    const tz = timezone || 'America/Los_Angeles';
    try {
      const date = new Date(isoString);
      const datePart = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: tz,
      });
      const timePart = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: tz,
      });
      return `${datePart} ${timePart}`;
    } catch {
      return '';
    }
  };

  const getCityFromAddress = (address) => {
    if (!address) return '';
    const parts = address.split(',');
    if (parts.length > 2) return parts[parts.length - 3].trim();
    return '';
  };

  // Get space type display text
  const spaceTypeDisplay = (() => {
    if (trip.losTitle && trip.spaceTypeTitle) return { los: trip.losTitle, space: trip.spaceTypeTitle };
    if (trip.los_title && trip.space_type_title) return { los: trip.los_title, space: trip.space_type_title };
    const stData = spaceTypeMap[trip.space_type];
    if (stData) return { los: stData.title, space: stData.original_title || stData.title };
    return { los: trip.space_type || '', space: trip.space_type || '' };
  })();

  const spaceTypeLineFull = `${spaceTypeDisplay.los || 'N/A'} (${spaceTypeDisplay.space || '...'})`;

  const showBanner = deadhead > 0 || (eta !== undefined && eta !== null);

  return (
    <div
      className={`rounded-md ring-1 ring-main-base flex flex-col flex-shrink-0 text-xs text-main-secondary shadow relative transition-all overflow-hidden ${
        isHighlighted ? 'ring-2 ring-primary bg-primary-light/30' : (busyStyles?.card ? busyStyles.card : 'bg-main-base-50')
      }`}
      data-trip-id={trip.trip_id || trip.id}
    >
      {/* Free time: approx pickup − (prev dropoff+unload) − eta (multiload: shown on group Route header) */}
      {!trip.is_multiload && (
        <TripFreeTimeBanner trip={trip} driverTrips={driverTrips} timezone={timezone} multiloadRoutes={multiloadRoutes} />
      )}

      {/* 1. MILEAGE/ETA BANNER */}
      {showBanner && (
        <div className={`flex items-center text-xs p-2 justify-center text-center text-white ${busyStyles?.badge || 'bg-main-primary'}`}>
          <div className="flex-1">{(deadhead || 0).toFixed(2)} miles</div>
          <div className="w-8">
            <svg className="h-6 w-6 mx-auto" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </div>
          <div className="flex-1">{eta != null ? `${Math.round(eta)} mins` : 'N/A'}</div>
        </div>
      )}

      {/* 2. CARD CONTENT */}
      <div className="p-2">
        {/* Header Row: Trip Name + ID */}
        <div className="flex mt-2 items-start justify-between flex-wrap">
          <div className="flex flex-col">
            <div className="text-main-primary font-bold">
            {clientNameWithLeg(trip.client_name, trip)}
            </div>
            <div className="text-main-light">
              {trip.trip_id || trip.id}
            </div>
          </div>
          
          <div className="flex items-center gap-1 group-hover:opacity-100 transition-opacity">
            {trip.preferred_driver_info?.preferred_driver_name && (
              <span className="inline-flex items-center rounded-md bg-emerald-600 px-1.5 py-0.5 text-[9px] font-medium text-white" title="Client's preferred driver">
                Pref: {trip.preferred_driver_info.preferred_driver_name}
              </span>
            )}
            {(trip.driver_violation === true || trip.preferred_driver_violation === true) && (
              <span className="inline-flex items-center rounded-md bg-rose-600 px-1.5 py-0.5 text-[9px] font-medium text-white" title="Assignment violates preference or constraints">
                Driver Violation
              </span>
            )}
            {trip.account_type && (
               <span className="inline-flex items-center rounded-md bg-main-dark px-1.5 py-0.5 text-[9px] font-medium text-white">
                 {trip.account_type}
               </span>
            )}
            {hasEscorts && (
              <span className="inline-flex items-center gap-0.5 rounded-md bg-purple-600 px-1.5 py-0.5 text-[9px] font-black text-white" title={`${escortCount} escort${escortCount !== 1 ? 's' : ''}`}>
                <UserPlus className="h-2.5 w-2.5" strokeWidth={2.5} />
                {escortCount}
              </span>
            )}
            {busyStyles?.badge && (
              <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter ${busyStyles.badge}`}>
                {tripStatus === 'on way' || tripStatus === 'onway' ? 'On Way' : tripStatus.toUpperCase()}
              </span>
            )}
            {showMultiloadBadge && (
              <span className="inline-flex items-center rounded-md bg-main-primary px-1.5 py-0.5 text-[9px] font-medium text-white" title="Multiload position">
                {multiloadPosition}/{multiloadGroupSize}
              </span>
            )}
            {manuallyAdjusted && (
              <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-500 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Manually adjusted">
                Manual
              </span>
            )}
            {trip.new_assignment === true && (
              <span className="inline-flex items-center rounded-md bg-violet-600 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Newly assigned in this run">
                New
              </span>
            )}
            {(trip.assumedWillCallTime === true || trip.assumed_will_call_time === true) && (
              <span className="inline-flex items-center rounded-md bg-sky-600 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Will-call time assumed from historical pattern">
                WC
              </span>
            )}
            <span className="inline-flex items-center rounded-md bg-yellow-700 px-1.5 py-0.5 text-[9px] font-medium text-white">
              P({trip.sequence})
            </span>
          </div>
        </div>

        {/* Timeline: continuous dashed rail between pickup and dropoff pins */}
        <div className="mt-2 grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-2">
          {/* Pickup pin — rail from pin center to bottom of row */}
          <div className="relative flex h-full items-start justify-center">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-2.5 bottom-0 w-0 -translate-x-1/2 border-l border-dashed border-main-light"
            />
            <div className="relative z-[1] h-5 w-5 bg-container-base rounded-full overflow-hidden grid place-content-center flex-shrink-0">
              <svg className="w-4 h-4 text-main-success" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"></path>
                <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" strokeLinecap="round" strokeLinejoin="round"></path>
              </svg>
            </div>
          </div>
          <div className="min-w-0 pr-6 group/addr relative">
            <div className="text-main-secondary truncate" title={trip.pickup_address}>{trip.pickup_address || 'N/A'}</div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2">
              <div className="min-w-0 truncate tabular-nums text-main-light">{formatShortDateTime(trip.schedule_time)}</div>
              <PickupEstimateMeta trip={trip} timezone={timezone} />
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); copyToClipboard(trip.pickup_address, 'pickup'); }}
              className="absolute right-0 top-0 p-1 opacity-0 group-hover/addr:opacity-100 transition-opacity bg-white/80 rounded shadow-sm"
              title="Copy pickup address"
            >
              {copiedField === 'pickup' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-main-light" />}
            </button>
          </div>
          
          {/* Dropoff pin — rail only from row top to pin center (never past the pin) */}
          <div className="relative flex h-full items-start justify-center">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-0 h-2.5 w-0 -translate-x-1/2 border-l border-dashed border-main-light"
            />
            <div className="relative z-[1] h-5 w-5 bg-container-base rounded-full overflow-hidden grid place-content-center flex-shrink-0">
              <svg className="w-4 h-4 text-main-danger" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"></path>
                <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" strokeLinecap="round" strokeLinejoin="round"></path>
              </svg>
            </div>
          </div>
          <div className="min-w-0 pr-6 group/addr relative">
            <div className="text-main-secondary truncate" title={trip.dropoff_address}>{trip.dropoff_address || 'N/A'}</div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2">
              <div className="min-w-0 truncate tabular-nums text-main-light">{formatShortDateTime(trip.appointment_time)}</div>
              <DropoffEstimateMeta trip={trip} timezone={timezone} />
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); copyToClipboard(trip.dropoff_address, 'dropoff'); }}
              className="absolute right-0 top-0 p-1 opacity-0 group-hover/addr:opacity-100 transition-opacity bg-white/80 rounded shadow-sm"
              title="Copy dropoff address"
            >
              {copiedField === 'dropoff' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-main-light" />}
            </button>
          </div>
        </div>

        {/* Bottom Metadata Icons — one row, equal quarters, shared vertical alignment */}
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex min-w-0 items-center gap-x-2">
            <div className="flex min-w-0 flex-1 basis-0 items-center gap-x-1.5" title={spaceTypeLineFull}>
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-main-light" aria-hidden>
                <svg className="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01l-1.97 5.67c-.07.21-.11.43-.11.66v7.16c0 .83.67 1.5 1.5 1.5S6 20.33 6 19.5V19h12v.5c0 .82.67 1.5 1.5 1.5c.82 0 1.5-.67 1.5-1.5v-7.16c0-.22-.04-.45-.11-.66l-1.97-5.67zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5s1.5.67 1.5 1.5s-.67 1.5-1.5 1.5zM5 11l1.27-3.82c.14-.4.52-.68.95-.68h9.56c.43 0 .81.28.95.68L19 11H5z" fill="currentColor"></path></svg>
              </span>
              <div className="min-w-0 cursor-default truncate text-xs leading-tight text-main-light">
                <span className="text-main-dark">{spaceTypeDisplay.los || 'N/A'}</span> ({spaceTypeDisplay.space || '...'})
              </div>
            </div>
            <div className="flex min-w-0 flex-1 basis-0 items-center gap-x-1.5" title={`Miles: ${trip.trip_distance_miles || '0'} mi`}>
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-main-light" aria-hidden>
                <svg className="h-4 w-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M511.984 36.128C230.016 36.128.639 265.536.639 547.504c0 177.152 89.68 339.185 239.903 433.408c14.944 9.472 34.688 4.88 44.097-10.096s4.88-34.72-10.096-44.095c-54.096-33.952-99.04-78.048-133.424-128.88l33.552-19.376c15.311-8.848 20.56-28.4 11.712-43.711c-8.88-15.344-28.464-20.56-43.712-11.712l-33.6 19.391c-24.4-50.511-39.297-105.792-43.281-163.424h35.616c17.68 0 32-14.32 32-32s-14.32-32-32-32H65.95c4.24-58.687 19.776-114.304 44.56-164.592l32.16 18.56a31.745 31.745 0 0 0 15.97 4.288c11.055 0 21.807-5.744 27.743-16c8.847-15.312 3.6-34.88-11.712-43.713l-31.84-18.368c32.112-46.832 72.864-87.296 119.984-119.023l18.016 31.2c5.935 10.288 16.687 16 27.743 16a31.75 31.75 0 0 0 15.969-4.288c15.311-8.848 20.56-28.4 11.712-43.712l-17.953-31.072c49.329-23.792 103.68-38.656 160.976-42.816v39.872c0 17.68 14.32 32 32 32s32-14.32 32-32v-40c58.592 4.08 114.128 19.391 164.384 43.95l-17.36 30.049c-8.848 15.311-3.6 34.88 11.712 43.712a31.745 31.745 0 0 0 15.969 4.288c11.055 0 21.807-5.712 27.743-16l17.28-29.936a451.19 451.19 0 0 1 118.88 118.816l-29.968 17.312c-15.311 8.847-20.56 28.4-11.711 43.71c5.935 10.289 16.687 16 27.743 16c5.44 0 10.944-1.375 15.969-4.287l30.127-17.392C938.638 401.839 954 457.39 958.094 516H922.96c-17.68 0-32 14.32-32 32s14.32 32 32 32h35.12c-4.048 56.88-18.592 111.439-42.496 161.312l-31.68-18.288c-15.28-8.848-34.912-3.568-43.712 11.713c-8.848 15.311-3.6 34.88 11.712 43.712L883.68 796.8c-35.103 52.24-81.44 97.393-137.359 131.824c-15.055 9.28-19.712 29.008-10.464 44.032c6.065 9.808 16.529 15.216 27.28 15.216a31.896 31.896 0 0 0 16.753-4.752c152.464-93.904 243.472-256.784 243.472-435.632c0-281.952-229.408-511.36-511.376-511.36zm236.127 411.6c15.296-8.848 20.544-28.398 11.712-43.71c-8.832-15.296-28.416-20.544-43.712-11.696L542.287 492.674c-9.28-5.248-19.856-8.496-31.28-8.496c-35.28 0-63.84 28.591-63.84 63.807c0 35.248 28.576 63.84 63.84 63.84c35.28 0 63.84-28.592 63.84-63.84c0-.064-.016-.144-.016-.209z" fill="currentColor"></path></svg>
              </span>
              <div className="min-w-0 truncate text-xs leading-tight text-main-light">
                {trip.trip_distance_miles || '0'} mi
              </div>
            </div>
            <div className="flex min-w-0 flex-1 basis-0 items-center gap-x-1.5">
              <TripRevenueLabel value={trip} textClassName="text-xs leading-tight" />
            </div>
            <div className="flex min-w-0 flex-1 basis-0 items-center gap-x-1.5">
              <CompanyNoteIconButton companyNote={getCompanyNote(trip)} />
            </div>
            <div className="flex min-w-0 flex-1 basis-0 items-center gap-x-1.5">
              {typeof onViewLogs === 'function' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canViewLogs) return;
                    const tid = trip.trip_id || trip.id;
                    if (tid) onViewLogs(String(tid));
                  }}
                  className={`
                    group inline-flex min-w-0 max-w-full items-center gap-x-1.5
                    m-0 border-0 bg-transparent p-0 font-inherit text-left
                    text-xs leading-tight text-main-light transition-colors
                    ${canViewLogs
                      ? 'hover:text-main-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-main-primary/30 focus-visible:ring-offset-1'
                      : 'cursor-not-allowed opacity-40'}
                  `}
                  title={canViewLogs ? 'View assignment logs for this trip' : 'Select a planning session first'}
                  aria-label={canViewLogs ? 'View logs for this trip' : 'Select a planning session to view logs'}
                >
                  <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-current" aria-hidden>
                    <ScrollText className="h-3.5 w-3.5" strokeWidth={2} />
                  </span>
                  <span className={`min-w-0 truncate leading-tight ${canViewLogs ? 'group-hover:text-main-primary' : ''}`}>
                    Logs
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UnassignedTripsPanel = ({ unassignedTrips, onClose, onViewLogs, canViewLogs, timezone, spaceTypeMap = {} }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone || 'America/Los_Angeles'
      });
    } catch {
      return isoString;
    }
  };

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredTrips = React.useMemo(() => {
    if (!debouncedSearchTerm) return unassignedTrips;
    return unassignedTrips.filter((trip) =>
      tripMatchesTripsPageSearch(trip, debouncedSearchTerm, spaceTypeMap, timezone)
    );
  }, [unassignedTrips, debouncedSearchTerm, spaceTypeMap, timezone]);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-[2100] transition-opacity backdrop-blur-sm" onClick={onClose} />
      
      <div className="fixed top-0 right-0 h-full w-full max-w-6xl bg-white shadow-2xl z-[2100] flex flex-col transform transition-transform duration-300 ease-in-out border-l border-gray-200">
        
        {/* Header - Matching Logs/Scheduler Pattern */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between shadow-sm z-20">
          <div className="flex items-center space-x-3">
            <div className="bg-red-500 p-2 rounded-lg shadow-sm">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Unassigned Trips</h2>
              <p className="text-xs text-gray-500 font-medium">
                {unassignedTrips.length} exception{unassignedTrips.length !== 1 ? 's' : ''} detected in current window
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-md transition-all text-gray-400 hover:text-gray-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search & Filter Bar */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex items-center gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by client, trip ID, address, time, LOS or space type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 py-1.5 text-xs rounded-md border-main-base focus:border-main-primary focus:ring-1 focus:ring-main-primary"
            />
          </div>
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            {filteredTrips.length} Result{filteredTrips.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Results Table */}
        <div className="flex-1 overflow-auto bg-gray-50/30">
          {filteredTrips.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-12 text-center">
              {unassignedTrips.length > 0 ? (
                <>
                  <p className="text-gray-900 font-semibold text-lg">No matching trips</p>
                  <p className="text-xs text-gray-500 mt-1">Try broadening your search criteria.</p>
                </>
              ) : (
                <>
                  <div className="bg-green-50 p-4 rounded-full mb-4">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </div>
                  <p className="text-gray-900 font-bold text-xl uppercase tracking-tight">Full Assignment Complete</p>
                  <p className="text-xs text-gray-500 mt-2 max-w-[300px]">Great! All trips in the selected window have been successfully scheduled to eligible drivers.</p>
                </>
              )}
            </div>
          ) : (
            <table className="min-w-full border-separate border-spacing-0 text-left bg-white">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="p-3 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider w-[56px]">#</th>
                  <th className="p-3 text-left text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider min-w-[140px]">Trip ID</th>
                  <th className="p-3 text-left text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">Client</th>
                  <th className="p-3 text-left text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider min-w-[320px]">Locations & Times</th>
                  <th className="p-3 text-left text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">Reason</th>
                  <th className="p-3 text-right text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">Dist</th>
                  <th className="p-3 text-right text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">Revenue</th>
                  <th className="p-3 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-gray-200 shadow-sm uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTrips.map((trip, idx) => (
                  <tr key={trip.trip_id || idx} className="hover:bg-gray-50/80 transition-colors h-14">
                    <td className="p-3 border-b border-r border-gray-100 text-center text-[11px] font-black text-gray-500 tabular-nums">
                      {idx + 1}
                    </td>
                    <td className="p-3 border-b border-r border-gray-100 uppercase">
                      <div className="text-xs font-bold text-gray-900 leading-tight whitespace-nowrap">{trip.trip_id || 'N/A'}</div>
                      <div className="flex gap-1 mt-1 items-center flex-wrap">
                        {trip.preferred_driver_info?.preferred_driver_name && (
                          <span className="px-1.5 py-0.5 rounded-[4px] text-[8px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100 uppercase tracking-tighter" title="Preferred Driver">PF</span>
                        )}
                        {(trip.assumedWillCallTime === true || trip.assumed_will_call_time === true) && (
                          <span className="inline-flex items-center rounded-md bg-sky-600 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Will-call time assumed from historical pattern">
                            WC
                          </span>
                        )}
                        <CompanyNoteIconButton companyNote={getCompanyNote(trip)} compact />
                      </div>
                    </td>
                    <td className="p-3 border-b border-r border-gray-100">
                      <div className="text-xs font-bold text-gray-900">{clientNameWithLeg(trip.client_name || 'N/A', trip)}</div>
                      <div className="mt-1">
                        <span className="px-1.5 py-0.5 rounded-[4px] text-[9px] font-black bg-gray-50 text-gray-500 border border-gray-200 uppercase tracking-tight">
                           {(() => {
                              if (trip.los_title && trip.space_type_title) return `${trip.los_title} (${trip.space_type_title})`;
                              if (trip.losTitle && trip.spaceTypeTitle) return `${trip.losTitle} (${trip.spaceTypeTitle})`;
                              return 'N/A';
                            })()}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 border-b border-r border-gray-100">
                      <div className="mb-2">
                        <div className="text-[11px] text-gray-900 font-bold truncate max-w-[300px]" title={trip.pickup_address}>
                          <span className="text-green-600 mr-1">↑</span> {trip.pickup_address || 'N/A'}
                        </div>
                        <div className="text-[10px] text-gray-400 font-medium ml-4 mt-0.5">
                          {formatTime(trip.schedule_time)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-900 font-bold truncate max-w-[300px]" title={trip.dropoff_address}>
                          <span className="text-red-600 mr-1">↓</span> {trip.dropoff_address || 'N/A'}
                        </div>
                        <div className="text-[10px] text-gray-400 font-medium ml-4 mt-0.5">
                          {formatTime(trip.appointment_time)}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 border-b border-r border-gray-100">
                      <span className="px-2 py-0.5 rounded-[4px] text-[10px] font-black bg-red-50 text-red-600 border border-red-100 uppercase tracking-tight">
                        {trip.reason || 'UNMET CONSTRAINTS'}
                      </span>
                    </td>
                    <td className="p-3 border-b border-r border-gray-100 text-right text-[11px] font-bold text-gray-900 tabular-nums">
                      {trip.trip_distance_miles || '0'} mi
                    </td>
                    <td className="p-3 border-b border-r border-gray-100 text-right text-[11px] font-bold text-gray-900 tabular-nums">
                      {formatTripRevenue(trip)}
                    </td>
                    <td className="p-3 border-b border-gray-100 text-center">
                       {onViewLogs && (
                        <button
                          onClick={() => onViewLogs(trip.trip_id)}
                          disabled={!canViewLogs}
                          className="px-3 py-1.5 bg-main-primary text-white rounded-md text-[10px] font-black uppercase tracking-widest shadow-sm hover:bg-main-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          View Logs
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Footer Audit Message */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3 text-gray-500">
             <AlertTriangle className="w-4 h-4 text-red-400" />
             <p className="text-[10px] font-medium leading-relaxed opacity-80">
                Trips appearing here failed all eligible driver constraints or encountered capacity conflicts. Use "View Logs" to audit specific rejection rationales for each trip.
             </p>
          </div>
        </div>
      </div>
    </>
  );
};


function isEstimateRecalcLog(log) {
  return log?.action === 'recalculate_estimates' || log?.trip_id === 'recalculate_estimates';
}

const LogsPanel = ({
  logsData,
  logsLoading,
  tripCounts,
  onClose,
  initialSearch = '',
  timezone,
}) => {
  const [selectedDriver, setSelectedDriver] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState(initialSearch);
  const [isGrouped, setIsGrouped] = React.useState(true);

  // Update search query when initialSearch changes (e.g., when opening from unassigned trip)
  React.useEffect(() => {
    setSearchQuery(initialSearch);
  }, [initialSearch]);

  // Extract unique driver names from logs
  const driverNames = React.useMemo(() => {
    if (!logsData?.logs) return [];
    const drivers = [...new Set(
      logsData.logs
        .filter((log) => !isEstimateRecalcLog(log))
        .map(log => log.driver_name)
        .filter(Boolean)
    )];
    return drivers.sort();
  }, [logsData?.logs]);

  // Filter logs based on selected driver AND search query across all columns
  const filteredLogs = React.useMemo(() => {
    if (!logsData?.logs) return [];

    let filtered = logsData.logs.filter((log) => !isEstimateRecalcLog(log));

    // Filter by driver if selected
    if (selectedDriver) {
      filtered = filtered.filter(log => log.driver_name === selectedDriver);
    }

    // Filter by search query across all columns
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(log => {
        // Search across all relevant fields
        const searchFields = [
          log.timestamp,
          log.trip_id,
          log.trip_client,
          log.driver_name,
          log.deadhead_miles?.toString(),
          log.trip_distance?.toString(),
          log.cost_base_distance?.toString(),
          log.cost_fleet_flexibility?.toString(),
          log.cost_idle_time?.toString(),
          log.cost_late_pickup?.toString(),
          log.cost_workload?.toString(),
          log.total_cost?.toString(),
          log.assignment_reason,
          log.assigned?.toString(),
          log.hard_constraints_passed?.toString()
        ].filter(Boolean); // Remove null/undefined

        // Check if any field contains the search query
        return searchFields.some(field =>
          field.toLowerCase().includes(query)
        );
      });
    }

    // Most recent first
    return [...filtered].sort((a, b) => {
      const tA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tB - tA;
    });
  }, [logsData?.logs, selectedDriver, searchQuery]);

  return (
    <>
      {/* Overlay - z-[2100] to appear above sticky header (z-[2000]) */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[2100] transition-opacity"
        onClick={onClose}
      />

      {/* Sliding Panel - Max width for log visibility */}
      <div className="fixed top-0 right-0 h-full w-full max-w-6xl bg-white shadow-2xl z-[2100] flex flex-col transform transition-transform duration-300 ease-in-out border-l border-gray-200">
        
        {/* Header - Aligned with UnifiedSchedulerDrawer Pattern */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between shadow-sm z-20">
          <div className="flex items-center space-x-3">
            <div className="bg-main-primary p-2 rounded-lg shadow-sm">
              <ClipboardList className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Assignment Logs</h2>
              <p className="text-xs text-gray-500 font-medium">
                {tripCounts?.totalCount ?? 0} trips processed • {tripCounts?.assignedCount ?? 0} assigned
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
             <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-200 rounded-md transition-all text-gray-400 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters & Search - Standardized UI */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex items-center gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search logs by trip, client or driver..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 py-1.5 text-xs rounded-md border-main-base focus:border-main-primary focus:ring-1 focus:ring-main-primary"
            />
          </div>

          <div className="flex items-center gap-4">
            {/* Driver Filter */}
            {driverNames.length > 0 && (
              <div className="flex items-center space-x-2">
                <select
                  id="driver-filter"
                  value={selectedDriver}
                  onChange={(e) => setSelectedDriver(e.target.value)}
                  className="text-xs py-1.5 focus:ring-main-primary focus:border-main-primary rounded-md border-main-base"
                >
                  <option value="">All Drivers ({logsData?.logs?.length || 0})</option>
                  {driverNames.map(driver => (
                    <option key={driver} value={driver}>
                      {driver} ({logsData?.logs?.filter(log => log.driver_name === driver).length || 0})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* View Mode Toggle - Pill style */}
            <div className="flex items-center bg-gray-100/80 p-1 rounded-lg">
              <button
                onClick={() => startTransition(() => setIsGrouped(false))}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${!isGrouped
                  ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' 
                  : 'text-gray-600 hover:text-gray-900'}`}
              >
                Flat View
              </button>
              <button
                onClick={() => startTransition(() => setIsGrouped(true))}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${isGrouped
                  ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' 
                  : 'text-gray-600 hover:text-gray-900'}`}
              >
                Group by Trip
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {logsLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <span className="ml-3 text-textPrimary">Loading logs...</span>
            </div>
          ) : logsData && logsData.error ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center max-w-lg">
                <div className="mb-4 flex justify-center"><AlertTriangle className="w-10 h-10 text-amber-500" /></div>
                <p className="text-error text-lg">Error loading logs</p>
                <p className="text-textMuted text-sm mt-2">{logsData.error}</p>
                <p className="text-textMuted text-xs mt-2">Result ID: {logsData.result_id}</p>

                {logsData.error.includes('permission-denied') && (
                  <div className="mt-4 p-3 bg-info-tint rounded-lg">
                    <p className="text-xs text-primary">
                      <strong>Firebase Permission Issue:</strong> Check security rules allow read access to assignment_logs collection.
                    </p>
                  </div>
                )}

                {logsData.error.includes('index') && (
                  <div className="mt-4 p-3 bg-warning-tint rounded-lg">
                    <p className="text-xs text-primary">
                      <strong>Firebase Index Required:</strong> The query needs a composite index. Create it at:
                    </p>
                    <a
                      href="https://console.firebase.google.com/v1/r/project/ai-planner-d97fa/firestore/indexes?create_composite=Clhwcm9qZWN0cy9haS1wbGFubmVyLWQ5N2ZhL2RhdGFiYXNlcy8oZGVmYXVsdCkvY29sbGVjdGlvbkdyb3Vwcy9hc3NpZ25tZW50X2xvZ3MvaW5kZXhlcy9fEAEaDQoJcmVzdWx0X2lkEAEaEAoMYmF0Y2hfbnVtYmVyEAEaDAoIX19uYW1lX18QAQ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary underline hover:text-primary-hover mt-1 inline-block"
                    >
                      Create Composite Index in Firebase Console
                    </a>
                  </div>
                )}
              </div>
            </div>
          ) : logsData && logsData.logs && logsData.logs.length > 0 ? (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Results Summary - Fixed above scrollable table */}
              <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex items-center justify-between shadow-sm">
                <div>
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">
                    {filteredLogs.length} Evaluation{filteredLogs.length !== 1 ? 's' : ''} {selectedDriver && `for ${selectedDriver}`}
                  </h3>
                  <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                    Result ID: {logsData.result_id.slice(0, 12)}... {selectedDriver && `• Filtered from ${logsData.log_count} total`}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-right">
                  <div>
                    <p className="text-[10px] font-black text-gray-900 uppercase tracking-wider tabular-nums leading-none">
                      {tripCounts?.assignedCount ?? 0} / {tripCounts?.totalCount ?? 0}
                    </p>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter mt-1">ASSIGNED TRIPS</p>
                  </div>
                  <div className="w-px h-8 bg-gray-100"></div>
                  <div>
                    <p className="text-[10px] font-black text-red-600 uppercase tracking-wider tabular-nums leading-none">
                      {tripCounts?.unassignedCount ?? 0}
                    </p>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter mt-1">UNASSIGNED</p>
                  </div>
                </div>
              </div>

              {/* Table area — LogsTable owns scroll + flat-view footer */}
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-gray-50/30">
                <LogsTable 
                  logs={filteredLogs} 
                  timezone={timezone} 
                  groupedByTrip={isGrouped}
                  setGroupedByTrip={setIsGrouped}
                />
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-full">
              <div className="text-center max-w-md">
                <div className="mb-4 flex justify-center"><BarChart3 className="w-10 h-10 text-gray-400" /></div>
                <p className="text-textPrimary text-lg mb-2">No assignment logs available</p>

                {logsData && logsData.result_id ? (
                  <div className="space-y-2">
                    <p className="text-textMuted text-sm">
                      Planning session found but no logs stored.
                    </p>
                    <p className="text-textMuted text-xs bg-surface p-2 rounded font-mono">
                      Result ID: {logsData.result_id}
                    </p>
                    <div className="text-xs text-textMuted space-y-1">
                      <p><strong>Possible reasons:</strong></p>
                      <ul className="text-left list-disc list-inside space-y-1">
                        <li>No new assignments recorded in this run</li>
                        <li>Logs were stored before the hybrid system was implemented</li>
                        <li>Firebase security rules prevent access to results or subcollections</li>
                        <li>Network connectivity issues</li>
                        <li>Logs stored in subcollection but main document not updated</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-textMuted text-sm">
                      {logsData.result_id ? 'Selected session has no logs.' : 'No planning session selected.'}
                    </p>
                    <div className="text-xs text-textMuted space-y-1">
                      <p><strong>To see logs:</strong></p>
                      <ol className="text-left list-decimal list-inside space-y-1">
                        <li>Select a planning session from the dropdown above</li>
                        <li>Ensure the planner was run with Firebase logging enabled</li>
                        <li>Check browser console for detailed error messages</li>
                      </ol>
                    </div>
                  </div>
                )}


              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const DEFAULT_FLAT_LOGS_PAGE_SIZE = 100;
const MAX_FLAT_LOGS_PAGE_SIZE = 1000;
const FLAT_LOGS_PAGE_SIZE_PRESETS = [50, 100, 250, 500, 1000];

const LogsTable = ({ logs, timezone, groupedByTrip, setGroupedByTrip }) => {
  const [showCosts, setShowCosts] = React.useState(false);
  const [expandedTrips, setExpandedTrips] = React.useState(new Set());
  const [flatPage, setFlatPage] = React.useState(0);
  const [flatPageSize, setFlatPageSize] = React.useState(DEFAULT_FLAT_LOGS_PAGE_SIZE);
  const flatLogs = logs || [];
  const flatPageSizeOptions = React.useMemo(() => {
    const total = flatLogs.length;
    if (total <= 1) return [];
    const maxAllowed = Math.min(MAX_FLAT_LOGS_PAGE_SIZE, total);
    return FLAT_LOGS_PAGE_SIZE_PRESETS.filter((size) => size > 1 && size <= maxAllowed);
  }, [flatLogs.length]);
  const flatTotalPages = Math.max(1, Math.ceil(flatLogs.length / flatPageSize));

  React.useEffect(() => {
    setFlatPage(0);
    setFlatPageSize((prev) => {
      const total = flatLogs.length;
      if (total <= 1) return prev;
      const maxAllowed = Math.min(MAX_FLAT_LOGS_PAGE_SIZE, total);
      if (prev > maxAllowed || prev <= 1) {
        return Math.min(DEFAULT_FLAT_LOGS_PAGE_SIZE, maxAllowed);
      }
      return prev;
    });
  }, [flatLogs.length, groupedByTrip]);

  React.useEffect(() => {
    if (flatPage > flatTotalPages - 1) {
      setFlatPage(Math.max(0, flatTotalPages - 1));
    }
  }, [flatPage, flatTotalPages]);

  const visibleFlatLogs = React.useMemo(() => {
    const start = flatPage * flatPageSize;
    return flatLogs.slice(start, start + flatPageSize);
  }, [flatLogs, flatPage, flatPageSize]);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Shift + L to toggle costs
      if (e.shiftKey && (e.key === 'L' || e.key === 'l')) {
        setShowCosts(prev => !prev);
      }
      // Shift + G to toggle grouping
      if (e.shiftKey && (e.key === 'G' || e.key === 'g')) {
        setGroupedByTrip(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setGroupedByTrip]);

  // Group logs by trip_id
  const groupedLogs = React.useMemo(() => {
    if (!groupedByTrip) return null;

    const groups = {};
    (logs || []).forEach(log => {
      const tripId = log.trip_id || 'UNKNOWN';
      if (!groups[tripId]) {
        groups[tripId] = {
          trip_id: tripId,
          trip_client: log.trip_client,
          logs: [],
          assigned: false,
          unassigned: false,
          assigned_driver: null,
          rejection_reasons: []
        };
      }
      groups[tripId].logs.push(log);

      // Track assignment status (include manual_move as assigned)
      if (log.action === 'manual_move' || log.assigned === 'True' || log.assigned === true) {
        groups[tripId].assigned = true;
        groups[tripId].assigned_driver = log.driver_name || groups[tripId].assigned_driver;
      } else {
        groups[tripId].unassigned = true;
        if (log.assignment_reason && !log.assignment_reason.includes('ASSIGNED') && log.assignment_reason !== 'Evaluating') {
          groups[tripId].rejection_reasons.push({
            driver: log.driver_name || 'Unknown',
            reason: log.assignment_reason
          });
        }
      }
    });

    const result = Object.values(groups);
    result.forEach(g => {
      g.logs.sort((a, b) => {
        const tA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return tB - tA;
      });
    });
    return result;
  }, [logs, groupedByTrip]);

  const formatCost = (value) => {
    return parseFloat(value || 0).toFixed(2);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZone: timezone || 'America/Los_Angeles'
      });
    } catch {
      return isoString;
    }
  };

  const toggleTripExpansion = (tripId) => {
    setExpandedTrips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  };

  // If grouping is enabled and we have grouped logs, show grouped view
  if (groupedByTrip && groupedLogs) {
    return (
      <div className="flex-1 overflow-auto bg-gray-50/50 p-6 space-y-4">

        {groupedLogs.map((group) => {
          const isExpanded = expandedTrips.has(group.trip_id);
          const isUnassigned = group.unassigned && !group.assigned;
          const driverEvaluations = group.logs.filter(log =>
            log.driver_name &&
            (log.assigned === 'False' || log.assigned === false) &&
            log.assignment_reason &&
            !log.assignment_reason.includes('ASSIGNED') &&
            log.assignment_reason !== 'Evaluating'
          );

          return (
            <div
              key={group.trip_id}
              className={`bg-white rounded-xl shadow-sm border transition-all duration-200 overflow-hidden ${isUnassigned
                ? 'border-red-200 hover:border-red-300'
                : 'border-gray-200 hover:border-main-primary/30'
                }`}
            >
              {/* Trip Summary Header */}
              <div
                className="p-4 cursor-pointer hover:bg-opacity-80 transition-colors"
                onClick={() => toggleTripExpansion(group.trip_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${isUnassigned ? 'bg-red-50' : 'bg-main-primary/5'}`}>
                       {isExpanded ? <ChevronDown className={`w-5 h-5 ${isUnassigned ? 'text-red-500' : 'text-main-primary'}`} /> : <ChevronRight className={`w-5 h-5 ${isUnassigned ? 'text-red-400' : 'text-main-primary/60'}`} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{group.trip_id}</span>
                        {isUnassigned && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-black bg-red-50 text-red-600 border border-red-100 uppercase tracking-wider">UNASSIGNED</span>
                        )}
                        {group.assigned && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-black bg-main-primary/10 text-main-primary border border-main-primary/20 uppercase tracking-wider">
                            ASSIGNED to {group.assigned_driver}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 font-medium mt-0.5">
                        Client: {group.trip_client} • {group.logs.length} evaluation{group.logs.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  {isUnassigned && driverEvaluations.length > 0 && (
                    <div className="text-[11px] font-bold text-red-500 bg-red-50 px-3 py-1 rounded-full border border-red-100">
                      {driverEvaluations.length} drivers rejected
                    </div>
                  )}
                </div>

                {/* Quick Summary of Rejection Reasons */}
                {isUnassigned && driverEvaluations.length > 0 && !isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap gap-1.5">
                      {Array.from(new Set(driverEvaluations.map(e => e.assignment_reason))).slice(0, 4).map((reason, idx) => (
                        <span
                          key={idx}
                          className="text-[10px] font-bold bg-red-50/50 text-red-700 px-2 py-0.5 rounded-full border border-red-100"
                        >
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Expanded Driver Evaluations */}
              {isExpanded && (
                <div className="border-t border-gray-100 bg-white">
                  <div className="p-0">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-100 border-separate border-spacing-0">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-5 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-gray-100">Driver</th>
                            <th className="px-3 py-3 text-right text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-gray-100">Deadhead</th>
                            <th className="px-3 py-3 text-right text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-gray-100">Dist</th>
                            <th className="px-3 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-gray-100">Constraints</th>
                            {showCosts && (
                              <th className="px-3 py-3 text-right text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-gray-100">Cost</th>
                            )}
                            <th className="px-5 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-gray-100">Rationale</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {group.logs.map((log, idx) => {
                            const isManualMove = log.action === 'manual_move';
                            const isAssigned = isManualMove || log.assigned === 'True' || log.assigned === true;
                            const statusRowBg = isAssigned ? 'bg-main-primary/[0.02]' : 'hover:bg-gray-50';

                            return (
                              <tr
                                key={idx}
                                className={`transition-colors h-12 ${statusRowBg}`}
                              >
                                <td className="px-5 py-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-gray-900 leading-tight">
                                       {isManualMove ? (log.driver_name || '').replace(/^Manual:\s*/i, '') || 'N/A' : (log.driver_name || 'N/A')}
                                    </span>
                                    {isAssigned && !isManualMove && (
                                      <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-main-primary text-white uppercase tracking-wider shadow-sm">
                                        ASSIGNED
                                      </span>
                                    )}
                                    {isManualMove && (
                                      <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-orange-500 text-white uppercase tracking-wider shadow-sm">
                                        MANUAL
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-right text-[11px] font-medium text-gray-600">
                                  {isManualMove ? '–' : `${formatCost(log.deadhead_miles)} mi`}
                                </td>
                                <td className="px-3 py-2 text-right text-[11px] font-medium text-gray-600">
                                  {isManualMove ? '–' : `${formatCost(log.trip_distance)} mi`}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex justify-center gap-1 flex-wrap max-w-[120px] mx-auto">
                                    {[
                                      { val: log.constraint_gender, label: 'G', title: 'Gender' },
                                      { val: log.constraint_shift, label: 'S', title: 'Shift' },
                                      { val: log.constraint_fleet, label: 'F', title: 'Fleet' },
                                      { val: log.constraint_service, label: 'A', title: 'Area' },
                                      { val: log.constraint_funding, label: '$', title: 'Fund' },
                                      { val: log.constraint_availability, label: 'Av', title: 'Avail' },
                                      { val: log.constraint_breaks, label: 'B', title: 'Breaks' },
                                      { val: log.constraint_temporal, label: 'T', title: 'Temp' },
                                      { val: log.constraint_deadhead, label: 'D', title: 'Dist' }
                                    ].map((c, i) => (
                                      <div 
                                        key={i} 
                                        className={`w-5 h-5 flex items-center justify-center rounded text-[9px] font-black border tracking-tighter shadow-sm transition-all ${c.val === 'True' || c.val === true
                                          ? 'bg-main-primary/10 text-main-primary border-main-primary/20' 
                                          : 'bg-gray-50 text-gray-300 border-gray-100 opacity-60'}`}
                                        title={c.title}
                                      >
                                        {c.label}
                                      </div>
                                    ))}
                                    {log.hard_constraints_passed === 'False' && (
                                       <div 
                                        className="w-5 h-5 flex items-center justify-center rounded text-[9px] font-black border tracking-tighter shadow-sm bg-red-50 text-red-500 border-red-100"
                                        title="Hard Constraint Failed"
                                      >
                                        <X size={10} strokeWidth={4} />
                                      </div>
                                    )}
                                  </div>
                                </td>
                                {showCosts && (
                                  <td className="px-3 py-2 text-right text-[11px] font-bold text-gray-900">
                                    ${formatCost(log.total_cost)}
                                  </td>
                                )}
                                <td className="px-5 py-2">
                                  <span className={`text-[11px] font-medium ${isAssigned ? 'text-main-primary' : 'text-red-500'}`}>
                                    {log.assignment_reason || 'N/A'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Flat view (original table — paginated so large sessions do not freeze the browser)
  const flatRangeStart = flatLogs.length ? flatPage * flatPageSize + 1 : 0;
  const flatRangeEnd = Math.min((flatPage + 1) * flatPageSize, flatLogs.length);
  const showFlatPagination = flatLogs.length > 1;

  return (
    <div className="flex flex-col h-full min-h-0 w-full">
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="min-w-full border-separate border-spacing-0 text-left">
        <thead className="bg-gray-50 sticky top-0 z-30">
          <tr>
            <th className="sticky top-0 left-0 z-40 p-2 text-left text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider min-w-[140px]">Trip Details</th>
            <th className="sticky top-0 z-30 p-2 text-left text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider whitespace-nowrap">Driver</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">G</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">S</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">F</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">A</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">$</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">Av</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">B</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">T</th>
            <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">D</th>
            <th className="sticky top-0 z-30 p-2 text-right text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider whitespace-nowrap">Deadhead</th>
            <th className="sticky top-0 z-30 p-2 text-right text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider whitespace-nowrap">Trip Dist</th>
            {showCosts && (
              <th className="sticky top-0 z-30 p-2 text-center text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-r border-gray-200 shadow-sm uppercase tracking-wider">Costs</th>
            )}
            <th className="sticky top-0 z-30 p-2 text-left text-[10px] font-bold text-gray-700 bg-gray-50 border-b border-gray-200 shadow-sm uppercase tracking-wider">Rationale</th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {visibleFlatLogs.map((log, index) => {
            const rowIndex = flatPage * flatPageSize + index;
            const isManualMove = log.action === 'manual_move';
            const isAssigned = isManualMove || log.assigned === 'True' || log.assigned === true;
            const statusRowBg = isAssigned ? 'bg-main-primary/[0.02]' : 'hover:bg-gray-50';

            return (
              <tr
                key={`${log.trip_id || 'trip'}-${log.timestamp || rowIndex}-${log.driver_name || 'driver'}-${rowIndex}`}
                className={`group transition-colors h-14 ${statusRowBg}`}
              >
                <td className="sticky left-0 z-20 p-2 bg-white/80 group-hover:bg-gray-50/80 backdrop-blur-sm border-b border-r border-gray-100 shadow-[1px_0_0_0_rgba(0,0,0,0.05)] min-w-[140px]">
                  <div className="text-xs font-bold text-gray-900 leading-tight whitespace-nowrap">
                    {log.trip_id || `TRIP${String(rowIndex + 1).padStart(4, '0')}`}
                  </div>
                  <div className="text-[9px] text-gray-400 font-medium tracking-tighter mt-0.5">
                    {log.trip_client}
                  </div>
                </td>
                <td className="p-2 border-b border-r border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-900 whitespace-nowrap">
                      {isManualMove ? (log.driver_name || '').replace(/^Manual:\s*/i, '') || 'N/A' : (log.driver_name || 'N/A')}
                    </span>
                    {isAssigned && !isManualMove && (
                      <span className="px-1 py-0.5 rounded-[4px] text-[8px] font-black bg-main-primary text-white uppercase tracking-wider shadow-sm flex-shrink-0">
                        ASSIGNED
                      </span>
                    )}
                    {isManualMove && (
                      <span className="px-1 py-0.5 rounded-[4px] text-[8px] font-black bg-orange-500 text-white uppercase tracking-wider shadow-sm flex-shrink-0">
                        MANUAL
                      </span>
                    )}
                  </div>
                </td>
                {[
                  { val: log.constraint_gender, label: 'G' },
                  { val: log.constraint_shift, label: 'S' },
                  { val: log.constraint_fleet, label: 'F' },
                  { val: log.constraint_service, label: 'A' },
                  { val: log.constraint_funding, label: '$' },
                  { val: log.constraint_availability, label: 'Av' },
                  { val: log.constraint_breaks, label: 'B' },
                  { val: log.constraint_temporal, label: 'T' },
                  { val: log.constraint_deadhead, label: 'D' },
                ].map((c, i) => (
                  <td key={i} className="p-2 border-b border-r border-gray-100 text-center">
                    <div className="flex justify-center">
                      {c.val === 'True' || c.val === true ? (
                        <div className="bg-main-primary/10 p-0.5 rounded shadow-sm ring-1 ring-main-primary/20">
                          <Check className="w-2.5 h-2.5 text-main-primary" strokeWidth={4} />
                        </div>
                      ) : (
                        <div className="bg-gray-50 p-0.5 rounded shadow-sm ring-1 ring-gray-100 opacity-40">
                          <X className="w-2.5 h-2.5 text-gray-400" strokeWidth={4} />
                        </div>
                      )}
                    </div>
                  </td>
                ))}
                <td className="p-2 border-b border-r border-gray-100 text-right text-[11px] font-medium text-gray-600 tabular-nums">
                  {isManualMove ? '–' : `${formatCost(log.deadhead_miles)} mi`}
                </td>
                <td className="p-2 border-b border-r border-gray-100 text-right text-[11px] font-medium text-gray-600 tabular-nums">
                  {isManualMove ? '–' : `${formatCost(log.trip_distance)} mi`}
                </td>
                {showCosts && (
                  <td className="p-2 border-b border-r border-gray-100">
                    <div className="flex justify-between gap-4 tabular-nums">
                      <span className="text-[9px] text-gray-400 uppercase">Total:</span>
                      <span className="text-[11px] text-gray-900 font-bold">${formatCost(log.total_cost)}</span>
                    </div>
                  </td>
                )}
                <td className="p-2 border-b border-gray-100">
                  <span className={`text-[11px] font-medium ${isAssigned ? 'text-main-primary' : 'text-red-500'}`}>
                    {log.assignment_reason || '–'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {showFlatPagination && (
        <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.06)] z-50 relative">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-gray-500 tabular-nums">
              Rows {flatRangeStart}–{flatRangeEnd} of {flatLogs.length}
            </span>
            <div className="hidden sm:block w-px h-5 bg-gray-200" aria-hidden="true" />
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-600">Per page</span>
              <select
                value={flatPageSize}
                onChange={(e) => {
                  const nextSize = Number(e.target.value);
                  startTransition(() => {
                    setFlatPage(0);
                    setFlatPageSize(nextSize);
                  });
                }}
                className="px-3 py-1.5 text-xs font-semibold rounded-md border border-main-base text-main-dark bg-white hover:bg-main-base-50 focus:outline-none focus:ring-2 focus:ring-main-primary/30 cursor-pointer tabular-nums min-w-[72px]"
              >
                {flatPageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {flatTotalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFlatPage((p) => Math.max(0, p - 1))}
                disabled={flatPage === 0}
                className="px-3 py-1.5 text-xs font-semibold rounded-md border border-main-base text-main-dark hover:bg-main-base-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-600 tabular-nums min-w-[88px] text-center font-semibold">
                Page {flatPage + 1} / {flatTotalPages}
              </span>
              <button
                type="button"
                onClick={() => setFlatPage((p) => Math.min(flatTotalPages - 1, p + 1))}
                disabled={flatPage >= flatTotalPages - 1}
                className="px-3 py-1.5 text-xs font-semibold rounded-md border border-main-base text-main-dark hover:bg-main-base-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DriverEligibilityPanel = ({
  eligibilityData,
  eligibilityLoading = false,
  onClose,
}) => {
  const [filterMode, setFilterMode] = React.useState('all'); // 'all', 'eligible', 'ineligible'
  const [searchQuery, setSearchQuery] = React.useState('');

  // Filter drivers based on mode and search
  const filteredDrivers = React.useMemo(() => {
    if (!eligibilityData?.driver_details) return [];

    return eligibilityData.driver_details.filter(driver => {
      // Search filter
      const matchesSearch = !searchQuery ||
        driver.driver_name.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesFilter = filterMode === 'all' ||
        (filterMode === 'eligible' && driver.is_eligible) ||
        (filterMode === 'ineligible' && !driver.is_eligible);

      return matchesSearch && matchesFilter;
    });
  }, [eligibilityData?.driver_details, searchQuery, filterMode]);

  // Get stats
  const eligibleCount = eligibilityData?.eligible_drivers || 0;
  const ineligibleCount = eligibilityData?.ineligible_drivers || 0;
  const totalCount = eligibilityData?.total_drivers || 0;

  return (
    <>
      {/* Overlay - z-[2100] to appear above sticky header (z-[2000]) */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[2100] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sliding Panel - Aligned with UnifiedSchedulerDrawer layout */}
      <div className="fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-2xl z-[2100] flex flex-col transform transition-transform duration-300 ease-in-out border-l border-gray-200">
        
        {/* Header - Aligned with UnifiedSchedulerDrawer Pattern */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between shadow-sm z-20">
          <div className="flex items-center space-x-3">
            <div className="bg-main-primary p-2 rounded-lg shadow-sm">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Driver Eligibility</h2>
              <p className="text-xs text-gray-500 font-medium">
                {eligibleCount} eligible / {ineligibleCount} ineligible (of {totalCount} total)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded-md transition-all text-gray-400 hover:text-gray-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters & Search - Aligned with modern high-density look */}
        {!(eligibilityLoading && !eligibilityData?.driver_details) && (
        <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex items-center gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search drivers by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 py-1.5 text-xs rounded-md border-main-base focus:border-main-primary focus:ring-1 focus:ring-main-primary"
            />
          </div>

          <div className="flex items-center bg-gray-100/80 p-1 rounded-lg">
            <button
              onClick={() => setFilterMode('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterMode === 'all' 
                ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' 
                : 'text-gray-600 hover:text-gray-900'}`}
            >
              All ({totalCount})
            </button>
            <button
              onClick={() => setFilterMode('eligible')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterMode === 'eligible' 
                ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' 
                : 'text-gray-600 hover:text-gray-900'}`}
            >
              Eligible ({eligibleCount})
            </button>
            <button
              onClick={() => setFilterMode('ineligible')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterMode === 'ineligible' 
                ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' 
                : 'text-gray-600 hover:text-gray-900'}`}
            >
              Ineligible ({ineligibleCount})
            </button>
          </div>
        </div>
        )}

        {/* Content - High-Density Table View */}
        <div className="flex-1 overflow-hidden bg-gray-50 flex flex-col">
          {eligibilityLoading && !eligibilityData?.driver_details ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-main-primary" />
              <span className="ml-3 text-sm text-main-light">Loading eligibility data…</span>
            </div>
          ) : !eligibilityData ? (
             <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="bg-gray-100 p-4 rounded-full mb-4 inline-block">
                  <AlertTriangle className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-900 font-semibold text-lg">No eligibility data available</p>
                <p className="text-xs text-gray-500 mt-2 max-w-[280px]">Statistical eligibility tracking might be disabled for this planning session.</p>
              </div>
            </div>
          ) : filteredDrivers.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-gray-500">
              <p className="text-gray-900 font-semibold">No matching drivers found</p>
              <p className="text-xs mt-1">Try broading your search or changing filters.</p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-xs font-bold text-main-primary hover:underline px-4 py-2 border border-main-primary/20 rounded-lg"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-auto bg-white shadow-inner">
              <table className="min-w-full divide-y divide-gray-200 border-separate border-spacing-0">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-5 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200">Status</th>
                    <th className="px-5 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200">Driver Details</th>
                    <th className="px-2 py-4 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200" title="Shift Time OK">Shift</th>
                    <th className="px-2 py-4 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200" title="Fleet Selection OK">Fleet</th>
                    <th className="px-2 py-4 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200" title="Fleet Compatibility OK">Comp</th>
                    <th className="px-2 py-4 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200" title="Funding Source OK">Fund</th>
                    <th className="px-2 py-4 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200" title="Service Area Coverage OK">Area</th>
                    <th className="px-2 py-4 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200" title="Trip Distance Constraints OK">Dist</th>
                    <th className="px-2 py-4 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200" title="Availability OK">Avail</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/95 backdrop-blur-md border-b border-gray-200">Failure Rationale</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredDrivers.map((driver, index) => {
                    const statusRowBg = !driver.is_eligible ? 'bg-red-50/20' : 'hover:bg-gray-50';
                    return (
                      <tr 
                        key={driver.driver_id || index}
                        className={`transition-colors h-14 ${statusRowBg}`}
                      >
                        <td className="px-5 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider shadow-sm border ${driver.is_eligible 
                            ? 'bg-main-primary/10 text-main-primary border-main-primary/20' 
                            : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {driver.is_eligible ? 'ELIGIBLE' : 'FAILED'}
                          </span>
                        </td>
                        <td className="px-5 py-2 whitespace-nowrap">
                          <div className="text-xs font-bold text-gray-900 leading-tight">{driver.driver_name}</div>
                          <div className="text-[10px] text-gray-400 font-mono tracking-tighter mt-0.5">{driver.driver_id}</div>
                        </td>
                        
                        {/* Status Cells */}
                        {[
                          driver.has_valid_shift,
                          driver.has_valid_fleet,
                          driver.fleet_compatible,
                          driver.funding_source_ok,
                          driver.service_area_ok,
                          driver.trip_distance_ok !== false,
                          driver.availability_ok !== false
                        ].map((ok, i) => (
                          <td key={i} className="px-2 py-2 text-center">
                            <div className="flex justify-center">
                              {ok ? (
                                <div className="bg-main-primary/10 p-0.5 rounded shadow-sm ring-1 ring-main-primary/20">
                                  <Check className="w-2.5 h-2.5 text-main-primary" strokeWidth={4} />
                                </div>
                              ) : (
                                <div className="bg-red-100 p-0.5 rounded shadow-sm ring-1 ring-red-200">
                                  <X className="w-2.5 h-2.5 text-red-600" strokeWidth={4} />
                                </div>
                              )}
                            </div>
                          </td>
                        ))}

                        <td className="px-6 py-2">
                          {driver.failing_reasons && driver.failing_reasons.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 max-w-xs">
                              {driver.failing_reasons.map((reason, idx) => (
                                <span key={idx} className="inline-block bg-red-100/50 text-red-800 text-[9px] font-bold px-2 py-0.5 rounded-full border border-red-200">
                                  {reason}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-main-primary opacity-60">
                               <CheckCircle className="w-3 h-3" />
                               <span className="text-[10px] font-medium italic">Passed validation</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer - Professional Disclaimer */}
        <div className="px-8 py-5 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-start gap-3 text-gray-500">
             <div className="bg-gray-200 p-1.5 rounded-lg mt-0.5">
                <AlertTriangle className="w-4 h-4 text-gray-500" />
             </div>
             <div>
                <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest mb-1">Audit Protocol V2.1</p>
                <p className="text-[10px] font-medium leading-relaxed opacity-80">
                   Eligibility is computed across the selected planning window. Drivers failing any hard constraint (Shift, Fleet, Funding, or Service Area) are automatically excluded by the scheduler core to ensure compliance.
                </p>
             </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TripsPage;
